import React, { useEffect, useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonText,
  IonCard,
  IonCardContent,
  IonIcon,
  IonButtons,
  IonAvatar,
  IonRow,
  IonCol
} from '@ionic/react';
import { closeCircleOutline, walletOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import './InsufficientBalanceModal.css';

interface InsufficientBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  balanceError?: {
    required: number;
    available: number;
    deficit: number;
    message: string;
  };
}

const InsufficientBalanceModal: React.FC<InsufficientBalanceModalProps> = ({
  isOpen,
  onClose,
  balanceError
}) => {
  const history = useHistory();

  const handleGoToBilling = () => {
    onClose();
    // Don't emit close event when going to billing - user might come back
    try {
      // Try to use history if available
      // Use /app/billing (shorter path, redirects to /app/settings/billing)
      if (history && typeof history.push === 'function') {
        history.push('/app/billing');
      } else {
        // Fallback to window.location if history is not available
        window.location.href = '/app/billing';
      }
    } catch (error) {
      // If history.push fails, use window.location as fallback
      console.error('Navigation error:', error);
      window.location.href = '/app/billing';
    }
  };

  const handleClose = () => {
    onClose();
    // Emit event when modal is closed via cancel/close button
    // This allows other modals (like FitAnalysisModal) to close themselves
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('insufficient-balance-modal-closed', {
        detail: { action: 'cancel' }
      }));
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ion-text-center">
            <IonIcon icon={walletOutline} className="ion-margin-end" />
            Insufficient Balance
          </IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={handleClose}>
              <IonIcon icon={closeCircleOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonCard className="ion-text-center ion-margin-bottom">
          <IonCardContent>
            <IonAvatar className="ion-margin-bottom">
              <IonIcon icon={walletOutline} color="danger" size="large" />
            </IonAvatar>

            <IonText>
              <h2 className="ion-no-margin ion-margin-bottom" style={{ color: '#1e40af', fontSize: '1.25rem', fontWeight: '700' }}>Not Enough Credits</h2>
            </IonText>

            <IonText>
              <p className="ion-margin-bottom" style={{ color: '#1f2937', fontSize: '0.95rem' }}>
                {balanceError?.message || 'You don\'t have enough credits to perform this action.'}
              </p>
            </IonText>
          </IonCardContent>
        </IonCard>

        {balanceError && (
          <>
            <IonCard className="ion-margin-bottom">
              <IonCardContent className="ion-padding-vertical">
                <IonRow className="ion-align-items-center">
                  <IonCol size="6" className="ion-text-center">
                    <IonText>
                      <p className="ion-no-margin ion-margin-bottom" style={{ color: '#374151', fontSize: '0.9rem', fontWeight: '500' }}>Required</p>
                    </IonText>
                    <IonText>
                      <h3 className="ion-no-margin" style={{ color: '#dc2626', fontSize: '1.5rem', fontWeight: '700' }}>${((balanceError.required || 0) / 100).toFixed(2)}</h3>
                    </IonText>
                  </IonCol>
                  <IonCol size="6" className="ion-text-center">
                    <IonText>
                      <p className="ion-no-margin ion-margin-bottom" style={{ color: '#374151', fontSize: '0.9rem', fontWeight: '500' }}>Available</p>
                    </IonText>
                    <IonText>
                      <h3 className="ion-no-margin" style={{ color: '#16a34a', fontSize: '1.5rem', fontWeight: '700' }}>${((balanceError.available || 0) / 100).toFixed(2)}</h3>
                    </IonText>
                  </IonCol>
                </IonRow>
              </IonCardContent>
            </IonCard>

            {balanceError.deficit > 0 && (
              <IonCard 
                className="ion-margin-bottom"
                style={{
                  '--background': '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px'
                }}
              >
                <IonCardContent>
                  <IonText>
                    <p 
                      className="ion-no-margin ion-text-center" 
                      style={{ 
                        color: '#92400e', 
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        margin: 0
                      }}
                    >
                      You need ${(balanceError.deficit / 100).toFixed(2)} more credits
                    </p>
                  </IonText>
                </IonCardContent>
              </IonCard>
            )}
          </>
        )}

        <IonRow className="ion-justify-content-center">
          <IonCol size="auto">
            <IonButton fill="outline" onClick={handleClose}>
              Cancel
            </IonButton>
          </IonCol>
          <IonCol size="auto">
            <IonButton fill="solid" onClick={handleGoToBilling}>
              Buy Credits
            </IonButton>
          </IonCol>
        </IonRow>
      </IonContent>
    </IonModal>
  );
};

export default InsufficientBalanceModal;
